var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { ReplaySubject } from 'rxjs';
import { TranslationState } from './translation-state';
/**
 * The content service - resolves the content
 * @class ContentService
 */
var ContentService = /** @class */ (function () {
    /**
     * Setting up the ContentService
     * @param {string} selector
     * @memberOf ContentService
     */
    function ContentService(selector) {
        if (selector === void 0) { selector = ''; }
        this.DCR_QUERY_SELECTOR = '.dcr-data';
        this.MAPPING_QUERY_SELECTOR = '.dcr-mapping';
        this.CWP_COMPONENT_ATTR_SELECTOR = 'data-componentname';
        this.APPLICATION_CONTAINER = 'Application Container';
        this._dcrMapping = new Map();
        this._usedKeys = new Map();
        this._editLink = new Map();
        this._usedKeysChanges = new ReplaySubject(1);
        this.warned = false;
        this._showEditor = window.location.search.includes('showEditor');
        if (selector) {
            if (this._showEditor) {
                console.log("(\n          " + window.crypto.getRandomValues(new Uint32Array(1))[0] + ")\n          Content service got element selector in the constructor\n          " + selector);
            }
            this.initialiseDCRContentFromElemSelector(selector);
        }
        this.initTestDcr();
    }
    /**
     * Set session storage for testDcr feature
     * @see resolve
     * @memberOf ContentService
     */
    ContentService.prototype.initTestDcr = function () {
        var locationHref = window.location.href;
        if (locationHref.includes('testDcr=true')) {
            sessionStorage.setItem('testDcr', 'true');
        }
        else if (locationHref.includes('testDcr=false')) {
            sessionStorage.removeItem('testDcr');
        }
    };
    /**
     * Setting up the dcrContent
     * @param {any} content
     * @memberOf ContentService
     */
    ContentService.prototype.initialiseDCRContentFromObj = function (content) {
        this._dcrContent = content;
    };
    /**
     * initialise DCRContent from element selector
     * @param {string} selectorString
     * @memberOf ContentService
     */
    ContentService.prototype.initialiseDCRContentFromElemSelector = function (selectorString) {
        this.initialiseDCRContentFromElem(document.querySelector(selectorString));
    };
    /**
     * initialise DCRContent from element
     * @param {HTMLElement} element
     * @memberOf ContentService
     */
    ContentService.prototype.initialiseDCRContentFromElem = function (element) {
        if (this._dcrContent) {
            console.warn('Content service already initialised, ignoring subsequent call.');
            return;
        }
        /**
           * End DCR Object, which will iterate for any query.
           */
        var content = {};
        /**
           * This is to hold the DOM element we create on CWP side for containing the DCR JSON.
           * refer application-container.ftl in CWP to see the structure of dcr
           */
        var contentElement;
        var dcrMappingElement;
        try {
            var appContainer = this.findMyAppContainer(element);
            if (!appContainer) {
                console.warn('AppContainer root element in CWP not found, aborting content service setup.');
                return;
            }
            contentElement = appContainer.querySelector(this.DCR_QUERY_SELECTOR);
            dcrMappingElement = appContainer.querySelector(this.MAPPING_QUERY_SELECTOR);
            try {
                this.setEditLink(appContainer);
            }
            catch (e) {
                console.log('Failed to initialise edit mode :' + e);
            }
        }
        catch (e) {
            ContentService.errorFetchingDcrFromCwp(e);
        }
        if (contentElement) {
            content = contentElement.getAttribute('value');
        }
        else {
            console.error("DCR is not attached to the component", element);
        }
        this.settingDcrContentAndMapping(content, dcrMappingElement);
    };
    /**
     * setting up the dcr content and the dcr mapping
     * @param {string} content
     * @param {Element|null|undefined} dcrMappingElement
     * @memberOf ContentService
     */
    ContentService.prototype.settingDcrContentAndMapping = function (content, dcrMappingElement) {
        this._dcrContent = ContentService.isNotEmpty(content) ? JSON.parse(content) : content;
        if (dcrMappingElement) {
            var mappingValue = dcrMappingElement.getAttribute('value');
            this._dcrMapping = ContentService.isNotEmpty(mappingValue) ? JSON.parse(mappingValue) : mappingValue;
        }
    };
    /**
     * setting the editLink
     * @param {HTMLElement} appContainer
     * @memberOf ContentService
     */
    ContentService.prototype.setEditLink = function (appContainer) {
        var editLinkList = appContainer.querySelectorAll('.edit a');
        for (var _i = 0, editLinkList_1 = editLinkList; _i < editLinkList_1.length; _i++) {
            var elem = editLinkList_1[_i];
            var shadowElement = elem;
            while (shadowElement && shadowElement.parentElement && !shadowElement.dataset['dcr']) {
                shadowElement = shadowElement.parentElement;
            }
            var link = shadowElement.href
                .replace(ContentService.EDIT_REGEX, '$1')
                .replace('/iw-cc/edit?vpath=', '/iw-cc/command/iw.ccpro.launch_edit_form?vpath=');
            if (shadowElement && shadowElement.dataset['dcr']) {
                this._editLink.set(shadowElement.dataset['dcr'], link);
            }
            else {
                this._editLink.set('targetedDcr', link);
            }
        }
    };
    /**
     * error handling for fetching DCR from CWP
     * @param {any} error
     * @memberOf ContentService
     */
    ContentService.errorFetchingDcrFromCwp = function (error) {
        console.error('Error in fetching DCR from CWP component.', error);
        console.log('Chances are you are not running your code in CWP env. So not able to get the hold of the DCR.');
        console.log('For quick fix,try simulating the cwp env. by copying the component wrapper' +
            ' from DOM of CWP page and wrapping you component in your index.html. for example');
        console.log("<div data-componentname=\"Application Container\"\ndata-tracked-name=\"blackrock-for-advisors:BFAReportsComponent\">\n                  <input type=\"hidden\" class=\"dcr-data\"\n                  value=\"{&quot;label&quot;:&quot;Investing Insights &quot;,\n                  &quot;errorMessage&quot;:&quot;Leave Me alone&quot;,\n                  &quot;header&quot;:&quot;HEADER&quot;,&quot;label&quot;:&quot;MY LABEL&quot;,\n                  &quot;amount&quot;:&quot;300&quot;,\n                  &quot;title&quot;:&quot;My Way&quot;}\">\n                  <app-reports></app-reports>\n            </div>");
    };
    /**
     * @deprecated use contentService.get(key, default) instead
     * @return {Object} dcrContent
     * @memberOf ContentService
     */
    ContentService.prototype.getDcrContent = function () {
        console.warn('DEPRECATED: DO NOT USE THE DCR OBJECT DIRECTLY. Use contentService.get(key, default) instead!');
        var that = this;
        return this._showEditor ?
            new Proxy(this._dcrContent, {
                get: function (target, name) {
                    if (name.toString() !== 'hasOwnProperty') {
                        that.trackTranslationWithDcrContent(name, target);
                    }
                    ContentService.returnTarget(name, target);
                },
            }) :
            this._dcrContent;
    };
    /**
     * Tracking translation with dcr content
     * @param {string|number|symbol} name
     * @param {any} target
     * @memberOf ContentService
     */
    ContentService.prototype.trackTranslationWithDcrContent = function (name, target) {
        var dcrContent = this._dcrMapping.get(name) ?
            this._dcrMapping.get(name) :
            ContentService.DCR_TARGETED;
        var translationState = new TranslationState(name.toString(), target[name], '', this._editLink, dcrContent);
        this.trackTranslation(translationState);
    };
    /**
     * returning with the correct target
     * @return {string|null} target[name].value if exists otherwise target[name]
     * @param {string|number|symbol} name
     * @param {any} target
     * @memberOf ContentService
     */
    ContentService.returnTarget = function (name, target) {
        if (name in target) {
            return target[name].value ? target[name].value : target[name];
        }
        else {
            return null;
        }
    };
    /**
     * @return {DOMStringMap} dataset
     * @param {any} el
     * @memberOf ContentService
     */
    ContentService.prototype.getAppContainerProperties = function (el) {
        try {
            return this.findMyAppContainer(el).dataset;
        }
        catch (err) {
            return {};
        }
    };
    /**
     * translateDcrConstants
     * @param {Object} dcrConstants
     * @param {string} prefix
     * @param {Object|undefined} dcr
     * @memberOf ContentService
     */
    ContentService.prototype.translateDcrConstants = function (dcrConstants, prefix, dcr) {
        var _this = this;
        if (dcr === void 0) { dcr = this._dcrContent; }
        // TODO: remove @ts-ignore
        Object.keys(dcrConstants).forEach(function (key) {
            // @ts-ignore
            dcrConstants[key] = _this.resolve(prefix + key, dcrConstants[key], dcr).toString();
        });
    };
    /**
     * is Object not empty
     * @return {boolean} object.keys.length !== 0
     * @param {string|null} object
     * @memberOf ContentService
     */
    ContentService.isNotEmpty = function (object) {
        return object ? Object.keys(object).length !== 0 : false;
    };
    /**
     * find my app container
     * @return {HTMLElement} parentElement
     * @param {HTMLElement} componentElement
     * @memberOf ContentService
     */
    ContentService.prototype.findMyAppContainer = function (componentElement) {
        var parentElement = componentElement;
        while (parentElement &&
            parentElement.getAttribute(this.CWP_COMPONENT_ATTR_SELECTOR) !== this.APPLICATION_CONTAINER) {
            parentElement = parentElement.parentElement;
        }
        return parentElement;
    };
    /**
     * getting the resolved value
     * @return {TranslationState} resolved value
     * @param {string} dcrText
     * @param {Object|undefined} dcr
     * @memberOf ContentService
     */
    ContentService.prototype.getResolvedVal = function (dcrText, dcr) {
        if (dcr === void 0) { dcr = this._dcrContent; }
        var dcrTextArray = dcrText.split('|');
        var dcrExpression = dcrTextArray[0].trim();
        var dcrQuestionCharIndex = dcrExpression.indexOf('?');
        var dcrKey = dcrQuestionCharIndex > 0 ? dcrExpression.substr(0, dcrQuestionCharIndex).trim() : dcrExpression;
        var defaultValue = dcrQuestionCharIndex > -1 ? dcrExpression.substr(dcrQuestionCharIndex + 1).trim() : null;
        var resolvedValue = this.resolve(dcrKey, defaultValue, dcr, false);
        if (dcrTextArray[1]) {
            console.warn("Inline pipe declaration [" + dcrTextArray[1] + "] is no longer supported,\n      please consider modifying your DCR content if the value you observe is not in the expected format.\n      DCR resolved value: " + resolvedValue);
        }
        return resolvedValue;
    };
    /**
     * getting the resolved value
     * @return {string} resolved value
     * @param {string} dcrKey
     * @param {string} defaultValue
     * @param {Object|undefined} dcr
     * @param {boolean} trackKey
     * @memberOf ContentService
     */
    ContentService.prototype.get = function (dcrKey, defaultValue, dcr, trackKey) {
        if (dcr === void 0) { dcr = this._dcrContent; }
        if (trackKey === void 0) { trackKey = true; }
        return this.resolve(dcrKey, defaultValue, dcr, trackKey).toString();
    };
    /**
     * check it the dcr key exists
     * @return {boolean} dcrValue && dcrValue.toLowerCase() === 'true'
     * @param {string} dcrKey
     * @param {boolean} defaultValue
     * @memberOf ContentService
     */
    ContentService.prototype.getBoolean = function (dcrKey, defaultValue) {
        if (defaultValue === void 0) { defaultValue = false; }
        var dcrValue = this.get(dcrKey, defaultValue ? 'true' : 'false');
        return dcrValue && dcrValue.toLowerCase() === 'true';
    };
    /**
     * validating dcr
     * @return {Object} {...dcr}
     * @param {Object|undefined} dcr
     * @memberOf ContentService
     */
    ContentService.prototype.validateDcr = function (dcr) {
        if (!dcr) {
            if (!this.warned) {
                console.warn("Content Service hasn't been initialised properly!");
                this.warned = true;
            }
            return {};
        }
        return __assign({}, dcr);
    };
    /**
     * @description This is the function we resolve with the dcr key
     * @return {TranslationState} translation
     * @param {string} dcrKey
     * @param {string|null} defaultValue
     * @param {map<string,string>} dcr
     * @param {boolean} trackKey
     * @memberOf ContentService
     */
    ContentService.prototype.resolve = function (dcrKey, defaultValue, dcr, trackKey) {
        if (dcr === void 0) { dcr = this._dcrContent; }
        if (trackKey === void 0) { trackKey = true; }
        // Run ?testDcr=true in the URL to replace all dcr keys with "WORKS!". This gives you an easy check to see which dcr works.
        var testDcr = sessionStorage.getItem('testDcr') === 'true';
        if (testDcr && defaultValue !== 'true' && defaultValue !== 'false') {
            return new TranslationState(dcrKey, 'WORKS!', defaultValue, this._editLink, this._dcrMapping.get(dcrKey) ? this._dcrMapping.get(dcrKey) : ContentService.DCR_TARGETED);
        }
        dcr = this.validateDcr(dcr);
        if (!dcr[dcrKey]) {
            if (this._showEditor) {
                console.log("No matching key found for \"" + dcrKey + "\" in DCR, falling back to default if provided one.");
            }
        }
        var translation = new TranslationState(dcrKey, dcr[dcrKey], defaultValue, this._editLink, this._dcrMapping.get(dcrKey) ? this._dcrMapping.get(dcrKey) : ContentService.DCR_TARGETED);
        if (trackKey && this._showEditor) {
            this.trackTranslation(translation);
        }
        return translation;
    };
    /**
     * @description resolve with fallbacks
     * @return {TranslationState}
     * @param {string[]} dcrKeys
     * @param {string} defaultValue
     * @param {map<string,string>|undefined}dcr
     * @param {boolean} trackKey
     * @memberOf ContentService
     */
    ContentService.prototype.resolveWithFallbacks = function (dcrKeys, defaultValue, dcr, trackKey) {
        if (dcr === void 0) { dcr = this._dcrContent; }
        if (trackKey === void 0) { trackKey = true; }
        dcr = this.validateDcr(dcr);
        for (var _i = 0, dcrKeys_1 = dcrKeys; _i < dcrKeys_1.length; _i++) {
            var dcrKey = dcrKeys_1[_i];
            if (dcr[dcrKey]) {
                return this.resolve(dcrKey, defaultValue, dcr, trackKey);
            }
        }
        return this.resolve(defaultValue, defaultValue, dcr, trackKey);
    };
    /**
     * track translation
     * @param {TranslationState} translation
     * @memberOf ContentService
     */
    ContentService.prototype.trackTranslation = function (translation) {
        if (!this._usedKeys.has(translation.key)) {
            var temp = new Map(this._usedKeys);
            temp.set(translation.key, translation);
            this._usedKeysChanges.next(temp);
            this._usedKeys = temp;
        }
    };
    /**
     * get substituted values as string
     * @return {string} message
     * @param {string} messageOrKey
     * @param {Map<string, any>} map
     * @param {string} defaultText
     * @memberOf ContentService
     */
    ContentService.prototype.getSubstitutedValuesString = function (messageOrKey, map, defaultText) {
        var translation = this.resolve(messageOrKey, defaultText);
        var message = translation.toString();
        if (!translation.isValid) {
            message = messageOrKey;
        }
        map.forEach(function (value, key) {
            var replacement = value ? value.toString() : '';
            message = message.replace(key, replacement);
        });
        return message;
    };
    /**
     * get dcr key name
     * @return {string} textMarkup.trim().split('?')[0]
     * @param {string} textMarkup
     * @memberOf ContentService
     */
    ContentService.prototype.getDcrKeyName = function (textMarkup) {
        return textMarkup.trim().split('?')[0];
    };
    Object.defineProperty(ContentService.prototype, "showEditor", {
        /**
         * show editor
         * @return {boolean} showEditor
         * @memberOf ContentService
         */
        get: function () {
            return this._showEditor;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ContentService.prototype, "usedKeys", {
        /**
         * used keys
         * @return {Subject<Map<string, TranslationState>>} usedKeysChanges
         * @memberOf ContentService
         */
        get: function () {
            return this._usedKeysChanges;
        },
        enumerable: false,
        configurable: true
    });
    ContentService.DCR_TARGETED = 'targetedDCR';
    ContentService.EDIT_REGEX = new RegExp(".*'(http[^']*).*");
    return ContentService;
}());
export { ContentService };
//# sourceMappingURL=content.service.js.map